import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_EMAIL_BODY_VALUES } from '@/components/UnlayerEditorWrapper/constants';
import { useLatestRef } from '@/components/UnlayerEditorWrapper/useLatestRef';

/**
 * Owns the imperative lifecycle for the embedded Unlayer editor instance.
 * This hook keeps React props and the editor's internal state in sync without
 * re-registering listeners or re-creating the editor on each render.
 */
export const useUnlayerEditorLifecycle = ({
	editorRef,
	onChange,
	onEditorReady,
	mergeTags,
	initialDesign,
	designToLoad,
	displayMode,
	onProgrammaticDesignLoad,
}) => {
	const onChangeRef = useLatestRef(onChange);
	const onEditorReadyRef = useLatestRef(onEditorReady);
	const onProgrammaticDesignLoadRef = useLatestRef(onProgrammaticDesignLoad);
	const mergeTagsRef = useLatestRef(mergeTags);
	const initialDesignRef = useLatestRef(initialDesign);
	const designToLoadRef = useLatestRef(designToLoad);
	const hasLoadedInitialDesignRef = useRef(false);
	const lastLoadedDesignRef = useRef(null);
	const [editorReadyRevision, setEditorReadyRevision] = useState(0);

	// Export the editor's latest html and design snapshot back to the parent form.
	// Unlayer is stateful, so the parent cannot derive this from React props alone.
	const flushDesignChange = useCallback(() => {
		const unlayer = editorRef.current?.editor;
		if (!unlayer) return;

		unlayer.exportHtml((data) => {
			onChangeRef.current?.({
				html: data?.html ?? '',
				design: data?.design ?? null,
			});
		});
	}, [editorRef, onChangeRef]);

	// Programmatic loadDesign calls are asynchronous inside Unlayer. Deferring the export
	// by one tick gives the editor time to finish applying the design before we read it back.
	const flushDesignChangeAfterProgrammaticLoad = useCallback(() => {
		setTimeout(() => {
			flushDesignChange();
		}, 0);
	}, [flushDesignChange]);

	// Prime the editor once it is ready: inject merge tags, load the first available design,
	// apply email defaults for a blank document, then wire the design update listener.
	const handleReady = useCallback(() => {
		const unlayer = editorRef.current?.editor;
		if (!unlayer) return;
		let readyFallbackTimeoutId = null;
		const markEditorReadyForExternalMutations = () => {
			if (readyFallbackTimeoutId) {
				clearTimeout(readyFallbackTimeoutId);
			}
			setEditorReadyRevision((current) => current + 1);
		};

		if (typeof unlayer.setMergeTags === 'function') {
			unlayer.setMergeTags(mergeTagsRef.current ?? {});
		}

		const readyDesign = designToLoadRef.current ?? initialDesignRef.current;
		if (
			!hasLoadedInitialDesignRef.current &&
			readyDesign &&
			typeof unlayer.loadDesign === 'function'
		) {
			const handleInitialProgrammaticLoadComplete = () => {
				markEditorReadyForExternalMutations();
				flushDesignChangeAfterProgrammaticLoad();
				onProgrammaticDesignLoadRef.current?.();
			};
			if (
				typeof unlayer.addEventListener === 'function' &&
				typeof unlayer.removeEventListener === 'function'
			) {
				const handleInitialDesignLoaded = () => {
					unlayer.removeEventListener('design:loaded', handleInitialDesignLoaded);
					handleInitialProgrammaticLoadComplete();
				};
				unlayer.addEventListener('design:loaded', handleInitialDesignLoaded);
				readyFallbackTimeoutId = setTimeout(() => {
					unlayer.removeEventListener('design:loaded', handleInitialDesignLoaded);
					handleInitialProgrammaticLoadComplete();
				}, 400);
			} else {
				readyFallbackTimeoutId = setTimeout(() => {
					handleInitialProgrammaticLoadComplete();
				}, 0);
			}
			unlayer.loadDesign(readyDesign);
			lastLoadedDesignRef.current = readyDesign;
			hasLoadedInitialDesignRef.current = true;
		} else {
			markEditorReadyForExternalMutations();
		}

		const shouldApplyDefaultEmailBodyValues = displayMode === 'email' && !readyDesign;
		if (shouldApplyDefaultEmailBodyValues && typeof unlayer.setBodyValues === 'function') {
			unlayer.setBodyValues(DEFAULT_EMAIL_BODY_VALUES);
		}

		onEditorReadyRef.current?.(unlayer);
		unlayer.addEventListener('design:updated', flushDesignChange);
	}, [
		designToLoadRef,
		displayMode,
		editorRef,
		flushDesignChange,
		initialDesignRef,
		mergeTagsRef,
		onEditorReadyRef,
	]);

	// Remove the registered Unlayer event listener when the wrapper unmounts.
	useEffect(
		() => () => {
			const unlayer = editorRef.current?.editor;
			if (!unlayer || typeof unlayer.removeEventListener !== 'function') return;
			unlayer.removeEventListener('design:updated', flushDesignChange);
		},
		[editorRef, flushDesignChange],
	);

	// Sync merge tags into the live editor whenever the resolved merge tag set changes.
	useEffect(() => {
		const unlayer = editorRef.current?.editor;
		if (!unlayer || typeof unlayer.setMergeTags !== 'function') return;
		unlayer.setMergeTags(mergeTags);
	}, [editorRef, mergeTags]);

	// Load a newly selected design when the parent swaps drafts or templates.
	// The ref guard prevents us from reloading the same object and resetting the editor cursor
	// after unrelated parent renders.
	useEffect(() => {
		if (!designToLoad || typeof designToLoad !== 'object') {
			return;
		}

		if (lastLoadedDesignRef.current === designToLoad) {
			return;
		}
		lastLoadedDesignRef.current = designToLoad;

		const unlayer = editorRef.current?.editor;
		if (!unlayer || typeof unlayer.loadDesign !== 'function') {
			return;
		}

		let didHandleDesignLoad = false;
		let designLoadFallbackTimeoutId = null;
		const handleProgrammaticDesignLoaded = () => {
			if (didHandleDesignLoad) {
				return;
			}
			didHandleDesignLoad = true;
			if (typeof unlayer.removeEventListener === 'function') {
				unlayer.removeEventListener('design:loaded', handleProgrammaticDesignLoaded);
			}
			if (designLoadFallbackTimeoutId) {
				clearTimeout(designLoadFallbackTimeoutId);
			}
			flushDesignChangeAfterProgrammaticLoad();
			onProgrammaticDesignLoadRef.current?.();
		};

		if (
			typeof unlayer.addEventListener === 'function' &&
			typeof unlayer.removeEventListener === 'function'
		) {
			unlayer.addEventListener('design:loaded', handleProgrammaticDesignLoaded);
			designLoadFallbackTimeoutId = setTimeout(() => {
				handleProgrammaticDesignLoaded();
			}, 400);
		}

		unlayer.loadDesign(designToLoad);
		if (
			typeof unlayer.addEventListener !== 'function' ||
			typeof unlayer.removeEventListener !== 'function'
		) {
			handleProgrammaticDesignLoaded();
		}

		return () => {
			if (designLoadFallbackTimeoutId) {
				clearTimeout(designLoadFallbackTimeoutId);
			}
			if (typeof unlayer.removeEventListener === 'function') {
				unlayer.removeEventListener('design:loaded', handleProgrammaticDesignLoaded);
			}
		};
	}, [
		designToLoad,
		editorRef,
		flushDesignChangeAfterProgrammaticLoad,
		onProgrammaticDesignLoadRef,
	]);

	return {
		handleReady,
		editorReadyRevision,
		flushDesignChange,
		flushDesignChangeAfterProgrammaticLoad,
	};
};
